import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Loader2, ShieldCheck, Home, CheckCircle2, Target, Lightbulb } from "lucide-react";
import { IMAGES } from "@/data/scenes";

const ACCENT = "#4FA3D9";

const line = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.28 },
  }),
};

const ReflectCard = ({ icon, label, text, i, testId }) => (
  <motion.div variants={line} custom={i} className="rounded-3xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-sm" data-testid={testId}>
    <div className="mb-2.5 flex items-center gap-2.5" style={{ color: ACCENT }}>
      {icon}
      <span className="text-xs font-semibold uppercase tracking-[0.2em]">{label}</span>
    </div>
    <p className="text-lg leading-relaxed text-white/85 sm:text-xl">{text}</p>
  </motion.div>
);

export const Debrief = ({ summary, onRestart, onViewParent, onHome }) => {
  const loading = !summary;
  const child = summary?.child || {};

  return (
    <div className="relative h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] grain" data-testid="debrief-screen">
      <button onClick={onHome} data-testid="home-btn" aria-label="Home" className="fixed left-5 top-6 z-50 flex h-10 w-10 items-center justify-center rounded-full border border-white/12 bg-black/30 text-white/75 backdrop-blur-xl transition-colors hover:bg-black/50 hover:text-white sm:left-7">
        <Home size={16} />
      </button>

      <motion.div className="fixed inset-0" initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}>
        <img src={IMAGES.debrief} alt="" className="h-full w-full object-cover" />
      </motion.div>
      <div className="fixed inset-0 bg-[#0a0a0a]/72" />
      <div className="fixed inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/40" />

      <div className="relative z-10 mx-auto w-full max-w-2xl px-6 pt-20 pb-16 sm:px-10 sm:py-10">
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-start gap-4" data-testid="debrief-loading">
              <Loader2 size={22} className="animate-spin" style={{ color: ACCENT }} />
              <p className="text-base text-white/60">Taking a moment to reflect on your story…</p>
            </motion.div>
          ) : (
            <motion.div key="content" initial="hidden" animate="show" data-testid="debrief-content">
              <motion.p variants={line} custom={0} className="mb-4 text-xs font-medium uppercase tracking-[0.32em]" style={{ color: ACCENT }}>
                A moment to reflect
              </motion.p>
              <motion.h1 variants={line} custom={1} className="mb-6 font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
                You made it through.
              </motion.h1>

              <div className="space-y-3" data-testid="debrief-reflection">
                <ReflectCard i={2} testId="reflect-did-well" icon={<CheckCircle2 size={16} />} label="What you did well" text={child.did_well} />
                <ReflectCard i={3} testId="reflect-practise" icon={<Target size={16} />} label="Something to practise" text={child.practise} />
                <ReflectCard i={4} testId="reflect-try-next" icon={<Lightbulb size={16} />} label="Try this next time" text={child.try_next} />
              </div>

              <motion.div variants={line} custom={5} className="mt-8 border-t border-white/10 pt-6">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={onRestart}
                    data-testid="practise-again-btn"
                    className="group inline-flex items-center justify-center gap-3 rounded-full border px-7 py-4 text-base font-semibold uppercase tracking-[0.16em] transition-[color,background-color] duration-500 active:scale-[0.98]"
                    style={{ borderColor: ACCENT, color: ACCENT }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = ACCENT; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = ACCENT; }}
                  >
                    <RotateCcw size={16} className="transition-transform duration-500 group-hover:-rotate-180" />
                    Practice again
                  </button>
                  <button
                    onClick={onViewParent}
                    data-testid="view-parent-profile-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-4 text-base font-medium tracking-wide text-white/75 transition-colors duration-300 hover:border-white/30 hover:text-white active:scale-[0.98]"
                  >
                    <ShieldCheck size={16} className="text-white/50" />
                    View Practice Report
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Debrief;
